import React from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import type { UsageDevices as Devices } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { UsageEmpty } from './UsageParts';
import { percentOf } from './usageLib';

/**
 * Share of sessions per device type as one split bar with its key, then the most used browsers,
 * for the user page's side column. The overview has its own one-line version.
 */
export const DevicesContent = ({ devices }: { devices: Devices }) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const total = devices.types.reduce((sum, type) => sum + type.sessions, 0);
    const browserTotal = devices.browsers.reduce((sum, row) => sum + row.sessions, 0);
    const shades = [1, 0.5, 0.3, 0.15].map(level => alpha(theme.palette.primary.main, level));
    const types = devices.types.map((type, index) => ({
        ...type,
        label: t(`Modules.Admin.Usage.deviceTypes.${type.device_type}`),
        pct: percentOf(type.sessions, total),
        colour: shades[Math.min(index, shades.length - 1)],
    }));

    if (types.length === 0) return <UsageEmpty />;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
                role="img"
                aria-label={types.map(type => `${type.label} ${type.pct}%`).join(', ')}
                sx={{
                    display: 'flex',
                    height: 8,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                }}
            >
                {types.map(type => (
                    <Box
                        key={type.device_type}
                        sx={{ width: `${type.pct}%`, bgcolor: type.colour }}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {types.map(type => (
                    <Box
                        key={type.device_type}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                        <Box
                            aria-hidden="true"
                            sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: type.colour }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                            {type.label}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                            {type.pct}%
                        </Typography>
                    </Box>
                ))}
            </Box>
            {devices.browsers.slice(0, 4).map(row => (
                <Box
                    key={`${row.browser}-${row.os}-${row.device_type}`}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {row.os
                            ? t('Modules.Admin.Usage.deviceOn', {
                                  browser: row.browser,
                                  os: row.os,
                              })
                            : row.browser}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                        {percentOf(row.sessions, browserTotal)}%
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
